import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { useProjectTeamMembers, useUpdateProject } from '@/hooks'
import type { Project, UpdateProjectRequest } from '@/types'
import { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { ExtendedCreateProjectRequest, FormTeamMember } from '../form'
import { ProjectBasicInfoSection, ProjectTypeSection, TeamAssignmentSection } from '../form'
import { Check } from 'lucide-react'

interface EditProjectDialogProps {
  project: Project
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditProjectDialog({ project, open, onOpenChange }: EditProjectDialogProps) {
  const updateProject = useUpdateProject()
  const { data: teamMembers = [] } = useProjectTeamMembers(project.id)

  const methods = useForm<ExtendedCreateProjectRequest>({
    defaultValues: {
      name: project.name,
      description: project.description ?? '',
      client: project.client,
      type: project.type,
      industry: project.industry,
      technologiesFrom: Array.isArray(project.technologiesFrom) ? project.technologiesFrom : [],
      technologiesTo: Array.isArray(project.technologiesTo) ? project.technologiesTo : [],
      teamMembers: [],
    },
  })

  const { reset, setValue } = methods
  const initializedForProjectId = useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      initializedForProjectId.current = null
      return
    }

    if (initializedForProjectId.current === project.id) {
      return
    }

    initializedForProjectId.current = project.id

    reset({
      name: project.name,
      description: project.description ?? '',
      client: project.client,
      type: project.type,
      industry: project.industry,
      technologiesFrom: Array.isArray(project.technologiesFrom) ? project.technologiesFrom : [],
      technologiesTo: Array.isArray(project.technologiesTo) ? project.technologiesTo : [],
      teamMembers: [],
    })
  }, [open, project, reset])

  useEffect(() => {
    if (open && teamMembers.length > 0 && initializedForProjectId.current === project.id) {
      setValue('teamMembers', teamMembers.map(tm => ({
        userId: tm.userId,
        roleIds: tm.roles.map(r => r.id),
        displayName: tm.displayName,
        email: tm.email,
        status: tm.status,
      })))
    }
  }, [open, teamMembers, project.id, setValue])

  const validateTeamMembers = (teamMembersList: FormTeamMember[] | undefined): string | null => {
    if (!teamMembersList || teamMembersList.length === 0) {
      return 'At least one team member is required'
    }

    for (let i = 0; i < teamMembersList.length; i++) {
      const member = teamMembersList[i]

      if (!member.userId) {
        return `Team member ${i + 1}: User ID is required`
      }

      if (!member.roleIds || member.roleIds.length === 0) {
        return `Team member "${member.displayName}": At least one role is required`
      }

      for (const roleId of member.roleIds) {
        if (!roleId) {
          return `Team member "${member.displayName}": Role ID cannot be empty`
        }
      }
    }

    return null
  }

  const onSubmit = async (data: ExtendedCreateProjectRequest) => {
    const teamMembersError = validateTeamMembers(data.teamMembers)
    if (teamMembersError) {
      methods.setError('teamMembers', {
        type: 'manual',
        message: teamMembersError,
      })
      return
    }

    try {
      const description = data.description?.trim()

      const teamMembersPayload = data.teamMembers.map(m => ({
        userId: m.userId,
        roleIds: m.roleIds,
      }))

      const payload: UpdateProjectRequest = {
        description: description || undefined,
        teamMembers: teamMembersPayload,
      }

      await updateProject.mutateAsync({ projectId: project.id, data: payload })
      toast.success('Project updated successfully')
      onOpenChange(false)
    } catch (error) {
      const apiError = error as {
        response?: {
          data?: {
            message?: string
            errors?: Array<{ field?: string; errorMessage?: string }>
          }
        }
      }

      const validationErrors = apiError?.response?.data?.errors
      if (validationErrors && validationErrors.length > 0) {
        const teamMemberError = validationErrors.find(e =>
          e.field?.toLowerCase().includes('teammember')
        )
        if (teamMemberError) {
          methods.setError('teamMembers', {
            type: 'manual',
            message: teamMemberError.errorMessage || 'Team member validation failed',
          })
        }

        const firstError = validationErrors[0]
        toast.error(firstError.errorMessage || 'Validation failed')
      } else {
        const message = apiError?.response?.data?.message || 'Failed to update project'
        toast.error(message)
      }
    }
  }

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      reset()
    }
    onOpenChange(value)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">Edit Project</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-input-background">
          <div className="max-w-4xl mx-auto py-8 px-6">
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Project Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <ProjectBasicInfoSection mode="edit" />
                    <Separator />
                    <ProjectTypeSection mode="edit" />
                    <Separator />
                    <TeamAssignmentSection />
                  </CardContent>
                </Card>
                <div className="mt-6 flex justify-end gap-3">
                  <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateProject.isPending}
                  >
                    <Check className="h-5 w-5" />
                    {updateProject.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
